import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ApiError,
  formatDate,
  formatMoney,
  type Holding,
  type OptionContract,
  type OptionStrategy,
  type StrategyKind,
} from '@greencandle/api-client'
import { api } from '../api'
import { Money, Quantity, Unknown } from '../components/Figure'
import { OptionLadder } from '../charts/OptionLadder'
import { PayoffDiagram } from '../charts/PayoffDiagram'

type Grouping = 'underlying' | 'expiry' | 'flat'

/** An option holding, with its contract known to be present. */
type OptionHolding = Holding & { option: OptionContract }

/**
 * The option book, grouped by the thing it is written on.
 *
 * <p>Options do not belong in a holdings table sorted by market value. Nothing quotes them
 * (FR-D6 is a later phase), so every one of them arrives unvalued, and the questions a reader
 * actually has are about the contract rather than the price: how much of this underlying am I
 * committed to, in which direction, and when does it roll off.
 *
 * <p>Grouping by underlying is what makes those answerable. Eighteen separate contract lines
 * say nothing; four underlyings with their calls and puts netted against each other say what
 * the book is. A vertical spread is two rows in a holdings table and one position here.
 */
export function OptionsPage() {
  const [accountId, setAccountId] = useState<string | null>(null)
  const [grouping, setGrouping] = useState<Grouping>('underlying')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [includeExpired, setIncludeExpired] = useState(false)

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.listAccounts() })
  const selected = accountId ?? accounts.data?.[0]?.id ?? null

  const holdings = useQuery({
    queryKey: ['holdings', selected],
    queryFn: () => api.holdings(selected!),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const strategies = useQuery({
    queryKey: ['optionsAnalysis', selected],
    queryFn: () => api.optionsAnalysis(selected!),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const account = accounts.data?.find((a) => a.id === selected) ?? accounts.data?.[0]

  const options = useMemo(() => {
    const rows = (holdings.data ?? []).filter(
      (h): h is OptionHolding => h.assetClass === 'OPTION' && h.option != null,
    )
    return includeExpired ? rows : rows.filter((h) => !h.option.expired)
  }, [holdings.data, includeExpired])

  const expiredCount = useMemo(
    () =>
      (holdings.data ?? []).filter(
        (h) => h.assetClass === 'OPTION' && h.option != null && h.option.expired,
      ).length,
    [holdings.data],
  )

  const groups = useMemo(() => buildGroups(options, grouping), [options, grouping])

  const visibleStrategies = useMemo(() => {
    const rows = strategies.data ?? []
    if (includeExpired) return rows
    const today = new Date().toISOString().slice(0, 10)
    return rows.filter((s) => s.expiry >= today)
  }, [strategies.data, includeExpired])

  if (accounts.isPending) return <p className="muted">Loading…</p>
  if (accounts.isError) return <Failed error={accounts.error} />
  if (!account) {
    return (
      <>
        <h1 className="page-title">Options</h1>
        <div className="notice">No accounts yet.</div>
      </>
    )
  }

  const currency = account.baseCurrency

  return (
    <>
      <h1 className="page-title">Options</h1>
      <p className="page-subtitle">{account.name}</p>

      <div className="filters">
        <select
          aria-label="Account"
          value={account.id}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {accounts.data.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.baseCurrency})
            </option>
          ))}
        </select>

        <div className="segmented" role="group" aria-label="Grouping">
          <button
            type="button"
            aria-pressed={grouping === 'underlying'}
            onClick={() => setGrouping('underlying')}
          >
            By underlying
          </button>
          <button
            type="button"
            aria-pressed={grouping === 'expiry'}
            onClick={() => setGrouping('expiry')}
          >
            By expiry
          </button>
          <button
            type="button"
            aria-pressed={grouping === 'flat'}
            onClick={() => setGrouping('flat')}
          >
            Flat
          </button>
        </div>

        {expiredCount > 0 && (
          <label className="inline-check">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
            />
            Show {expiredCount} expired
          </label>
        )}

        <span className="muted">
          {options.length} contract line{options.length === 1 ? '' : 's'} · {groups.length}{' '}
          {grouping === 'expiry' ? 'expiries' : grouping === 'flat' ? 'rows' : 'underlyings'}
        </span>
      </div>

      {holdings.isError ? (
        <Failed error={holdings.error} />
      ) : options.length === 0 ? (
        <div className="notice">
          This account holds no open option contracts.
          {expiredCount > 0 && !includeExpired && ' Expired contracts are hidden.'}
        </div>
      ) : (
        <>
          {strategies.isError ? (
            <div className="notice error" style={{ marginBottom: 16 }} role="alert">
              Strategies could not load.
            </div>
          ) : visibleStrategies.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <h2 className="card-title">Strategies</h2>
              <div className="grid">
                {visibleStrategies.map((s) => (
                  <StrategyCard
                    key={`${s.underlying}-${s.expiry}-${s.kind}-${s.legs.map((l) => l.strike).join(',')}`}
                    strategy={s}
                    currency={currency}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="card" style={{ marginBottom: 16 }}>
            <h2 className="card-title">Notional at risk, by expiry</h2>
            <OptionLadder holdings={options} currency={currency} />
          </div>

          <div className={`card${holdings.isFetching && !holdings.isPending ? ' refetching' : ''}`}>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{grouping === 'expiry' ? 'Expiry' : 'Underlying'}</th>
                    <th className="right">Contracts</th>
                    <th className="right">Net notional</th>
                    <th className="right">Cost basis</th>
                    <th className="right">Market value</th>
                    <th>Nearest expiry</th>
                  </tr>
                </thead>

                {groups.map((group) => {
                  const open = expanded === group.key || grouping === 'flat'
                  return (
                    <tbody key={group.key}>
                      {grouping !== 'flat' && (
                        <tr
                          className="group-row"
                          onClick={() => setExpanded(open ? null : group.key)}
                        >
                          <td>
                            <button type="button" className="linklike" aria-expanded={open}>
                              <span aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
                              <strong>{group.key}</strong>
                            </button>
                            <span className="muted"> · {group.mix}</span>
                          </td>
                          <td className="right">
                            {group.longs > 0 && (
                              <>
                                <span className="num">{group.longs}</span>
                                <span className="muted"> long</span>
                              </>
                            )}
                            {group.shorts > 0 && (
                              <>
                                <span className="num">{group.longs > 0 ? ' ' : ''}{group.shorts}</span>
                                <span className="muted"> short</span>
                              </>
                            )}
                          </td>
                          <td className="right">
                            <Money value={group.notional} currency={currency} />
                          </td>
                          <td className="right">
                            <Money value={group.costBasis} currency={currency} />
                          </td>
                          <td className="right">
                            {group.valued === group.rows.length ? (
                              <Money value={group.marketValue} currency={currency} />
                            ) : (
                              <Unknown
                                reason={
                                  group.valued === 0
                                    ? 'No provider quotes these contracts'
                                    : `${group.valued} of ${group.rows.length} priced`
                                }
                              />
                            )}
                          </td>
                          <td>
                            {group.nearest ? (
                              <>
                                {formatDate(group.nearest.expiry)}
                                <span className="muted">
                                  {' '}
                                  · {daysLabel(group.nearest.daysToExpiry)}
                                </span>
                              </>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                        </tr>
                      )}

                      {open &&
                        group.rows.map((row) => (
                          <tr key={row.instrumentId}>
                            <td>
                              <span className="num">{contractLabel(row, grouping)}</span>
                              {row.option.expired && <span className="chip">expired</span>}
                            </td>
                            <td className="right">
                              <Quantity value={row.quantity} />
                            </td>
                            <td className="right">
                              <Money value={notionalOf(row)} currency={row.currency} />
                            </td>
                            <td className="right">
                              <Money value={row.costBasis} currency={row.currency} />
                            </td>
                            <td className="right">
                              {row.marketValue != null ? (
                                <Money value={row.marketValue} currency={row.currency} />
                              ) : (
                                <Unknown reason={row.priceNote} />
                              )}
                            </td>
                            <td>
                              {formatDate(row.option.expiry)}
                              <span className="muted">
                                {' '}
                                · {daysLabel(row.option.daysToExpiry)}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  )
                })}
              </table>
            </div>
          </div>

          <p className="chart-note muted">
            Net notional is strike × multiplier × contracts, signed by direction. It is not
            delta-adjusted: a far out-of-the-money contract counts its full notional here while
            behaving like a fraction of it. Read it as commitment, not exposure.
          </p>
        </>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────── grouping

interface Group {
  key: string
  rows: OptionHolding[]
  longs: number
  shorts: number
  mix: string
  notional: string
  costBasis: string
  marketValue: string
  valued: number
  nearest: OptionContract | null
  /** Including expired contracts, so an all-expired group still sorts somewhere sensible. */
  earliestExpiry: string
}

/**
 * Sums are done in floating point and formatted back to strings.
 *
 * <p>Every figure crosses the wire as a string precisely so nothing rounds it on the way
 * (D-07), and that holds for the numbers this page *reports*: quantity, cost basis and market
 * value are rendered from the strings the server sent, untouched. What is computed here is
 * only the group total shown beside them — a summary of figures already on screen, never a
 * ledger entry and never sent anywhere. The alternative is a decimal library in the bundle to
 * add up eighteen numbers for a heading.
 */
function buildGroups(rows: OptionHolding[], grouping: Grouping): Group[] {
  if (grouping === 'flat') {
    const sorted = [...rows].sort(byExpiryThenStrike)
    return [{ ...summarise(sorted), key: 'All contracts', rows: sorted,
              earliestExpiry: sorted[0]?.option.expiry ?? '' }]
  }

  const buckets = new Map<string, OptionHolding[]>()
  for (const row of rows) {
    const key =
      grouping === 'underlying' ? row.option.underlyingSymbol : (formatDate(row.option.expiry) ?? row.option.expiry)
    buckets.set(key, [...(buckets.get(key) ?? []), row])
  }

  const groups = [...buckets.entries()].map(([key, group]) => {
    const sorted = [...group].sort(byExpiryThenStrike)
    // Read off the first row while it is known to be here; rows are already in expiry order.
    return { ...summarise(sorted), key, rows: sorted,
             earliestExpiry: sorted[0]?.option.expiry ?? '' }
  })

  // By underlying, the biggest commitment first — that is the order the questions come in.
  // By expiry, chronological, because that is what an expiry axis means.
  return grouping === 'expiry'
    ? groups.sort((a, b) => a.earliestExpiry.localeCompare(b.earliestExpiry))
    : groups.sort((a, b) => Math.abs(Number(b.notional)) - Math.abs(Number(a.notional)))
}

function summarise(rows: OptionHolding[]): Omit<Group, 'key' | 'rows' | 'earliestExpiry'> {
  let longs = 0
  let shorts = 0
  let calls = 0
  let puts = 0
  let notional = 0
  let costBasis = 0
  let marketValue = 0
  let valued = 0
  let nearest: OptionContract | null = null

  for (const row of rows) {
    const qty = Number(row.quantity)
    if (qty >= 0) longs += qty
    else shorts += -qty
    if (row.option.right === 'CALL') calls += 1
    else puts += 1

    notional += Number(notionalOf(row))
    costBasis += Number(row.costBasis)
    if (row.marketValue != null) {
      marketValue += Number(row.marketValue)
      valued += 1
    }
    // The nearest expiry still to come. An expired contract is not a deadline.
    if (!row.option.expired && (nearest === null || row.option.expiry < nearest.expiry)) {
      nearest = row.option
    }
  }

  const mix = [calls > 0 && `${calls} call${calls === 1 ? '' : 's'}`, puts > 0 && `${puts} put${puts === 1 ? '' : 's'}`]
    .filter(Boolean)
    .join(', ')

  return {
    longs,
    shorts,
    mix,
    notional: notional.toFixed(2),
    costBasis: costBasis.toFixed(2),
    marketValue: marketValue.toFixed(2),
    valued,
    nearest,
  }
}

/** Strike × multiplier × contracts, signed: what the position commits to, and which way. */
function notionalOf(row: OptionHolding): string {
  return (Number(row.option.strike) * Number(row.option.multiplier) * Number(row.quantity)).toFixed(2)
}

function byExpiryThenStrike(a: OptionHolding, b: OptionHolding): number {
  if (a.option.expiry !== b.option.expiry) return a.option.expiry < b.option.expiry ? -1 : 1
  const strike = Number(a.option.strike) - Number(b.option.strike)
  if (strike !== 0) return strike
  return a.option.right.localeCompare(b.option.right)
}

/**
 * A contract as a person says it: "84 CALL", or "NFLX 84 CALL" where the underlying is not
 * already the heading above it.
 *
 * <p>Not the symbol. `NFLX 260918C00084000` is an OCC identifier - correct, unique, and
 * unreadable at a glance, which is the one thing a row of a table has to be. The expiry is
 * already the last column, so it is not repeated here.
 */
function contractLabel(row: OptionHolding, grouping: Grouping): string {
  const strike = trimZeros(row.option.strike)
  const contract = `${strike} ${row.option.right}`
  return grouping === 'underlying' ? contract : `${row.option.underlyingSymbol} ${contract}`
}

/** 84.00 reads as 84; 12.50 keeps its half. */
function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value
}

function daysLabel(days: number): string {
  if (days < 0) return `${-days}d ago`
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `in ${days}d`
}

// ─────────────────────────────────────────────────────────────── strategy cards (FR-H11)

const KIND_LABEL: Record<StrategyKind, string> = {
  COLLAR: 'Collar',
  COVERED_CALL: 'Covered call',
  PROTECTIVE_PUT: 'Protective put',
  IRON_CONDOR: 'Iron condor',
  BUTTERFLY: 'Butterfly',
  VERTICAL_SPREAD: 'Vertical spread',
  STRADDLE: 'Straddle',
  STRANGLE: 'Strangle',
  SINGLE_LEG: 'Single leg',
  CUSTOM: 'Custom combination',
}

/**
 * One auto-detected strategy: name, legs, the payoff diagram, and the numbers that summarise
 * it. `netCost`/`maxProfit`/`maxLoss` are already signed and multiplier-scaled by the server -
 * this only decides how to read a null (an unbounded max, never "not available": the server
 * says which with `*Unbounded`, and the two must not be confused, per NFR-U4).
 */
function StrategyCard({ strategy, currency }: { strategy: OptionStrategy; currency: string }) {
  const legs = strategy.legs
    .map((l) => {
      const qty = Number(l.quantity)
      const direction = qty >= 0 ? 'Long' : 'Short'
      return `${direction} ${Math.abs(qty)} ${trimZeros(l.strike)} ${l.right}`
    })
    .join(' · ')

  const days = daysUntil(strategy.expiry)

  return (
    <div className="card">
      <h2 className="card-title">
        {strategy.underlying} · {KIND_LABEL[strategy.kind]}
      </h2>
      <p className="muted" style={{ marginTop: -8, marginBottom: 12, fontSize: 13 }}>
        {legs}
      </p>

      <PayoffDiagram strategy={strategy} currency={currency} />

      <div className="tiles" style={{ marginTop: 12, marginBottom: 0 }}>
        <Stat label={Number(strategy.netCost) < 0 ? 'Net credit' : 'Net debit'}>
          <Money value={String(Math.abs(Number(strategy.netCost)))} currency={currency} />
        </Stat>
        <Stat label="Max profit">
          <MaxFigure
            value={strategy.maxProfit}
            unbounded={strategy.maxProfitUnbounded}
            currency={currency}
          />
        </Stat>
        <Stat label="Max loss">
          <MaxFigure
            value={strategy.maxLoss}
            unbounded={strategy.maxLossUnbounded}
            currency={currency}
          />
        </Stat>
        <Stat label={strategy.breakevens.length === 1 ? 'Breakeven' : 'Breakevens'}>
          {strategy.breakevens.length === 0 ? (
            <span className="stat-value figure muted">—</span>
          ) : (
            <span className="stat-value figure">
              {strategy.breakevens.map((b) => formatMoney(b, currency)).join(' / ')}
            </span>
          )}
        </Stat>
        <Stat label="Expiry">
          <span className="stat-value figure">{daysLabel(days)}</span>
        </Stat>
      </div>
    </div>
  )
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-value figure">{children}</div>
    </div>
  )
}

/** Unbounded renders as text, never as a number and never as "not available" - there is no
 *  limit, which is a different fact from the server not knowing one. */
function MaxFigure({
  value,
  unbounded,
  currency,
}: {
  value: string | null
  unbounded: boolean
  currency: string
}) {
  if (unbounded) return <span className="stat-value figure">Unlimited</span>
  return <Money value={value} currency={currency} display="figure" />
}

function daysUntil(iso: string): number {
  const today = new Date().toISOString().slice(0, 10)
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.round((Date.parse(iso) - Date.parse(today)) / msPerDay)
}

function Failed({ error }: { error: unknown }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof ApiError ? (error.problem.detail ?? error.message) : 'Could not load.'}
    </div>
  )
}
